/**
 * @fileoverview Field-override bookkeeping for the generate wizard (MOR-2342
 * QA-08). A manual edit — including an intentional BLANK — is a value in its
 * own right: it survives switching presets or the primary-contact source,
 * saving, reopening and regeneration, until the analyst explicitly resets it.
 * "Absent" (never edited, key missing) and "explicitly empty" ("") are
 * therefore distinct everywhere these helpers are used.
 */

import { GALLAGHER_OUTPUT_ONLY_KEYS } from "~/convex/jobs/precedents/gallagherFieldConfig";
import type { DayOneContactDefault } from "@cvx/organizations/schema";

const EMPTY_KEY_SET: ReadonlySet<string> = new Set();

/**
 * Rebuild the wizard's overrides from previously generated documents.
 * Every saved value is kept, including an empty string, because a saved
 * blank only ever exists where the analyst cleared the field on purpose
 * (`mergeFieldValues` never writes empty fallbacks). For a key saved on
 * several documents the first document's value wins.
 */
export function collectSavedOverrides(
  precedents: ReadonlyArray<{ fieldValues?: unknown; templateKey?: string }>,
): Record<string, string> {
  return precedents.reduce<Record<string, string>>((acc, precedent) => {
    const saved = precedent.fieldValues;
    if (!saved || typeof saved !== "object") return acc;
    // A generated Gallagher document stores RESOLVED portal values under the
    // portal field keys. Where that key is only an output name (its source is
    // a different canonical key) the value is a result, not an input: it must
    // not come back as an override of the same-named canonical field
    // (`company_name`) or silently outrank the reviewed source (`firm_phone`
    // vs `contact_phone`) on the next generation (MOR-2342).
    const skip =
      precedent.templateKey === "form_gallagher"
        ? GALLAGHER_OUTPUT_ONLY_KEYS
        : EMPTY_KEY_SET;
    const entries = Object.entries(saved as Record<string, unknown>).filter(
      ([key, value]) =>
        typeof value === "string" && !(key in acc) && !skip.has(key),
    ) as Array<[string, string]>;
    return entries.length === 0
      ? acc
      : { ...acc, ...Object.fromEntries(entries) };
  }, {});
}

/** The slice of a generated document the wizard reads when it reopens. */
export interface SavedWizardDocument {
  templateKey?: string;
  fieldValues?: unknown;
  /** Manual edits stamped at generation (MOR-2358); absent on older rows. */
  fieldOverrides?: Record<string, string>;
  /** Contact person the document was generated with (MOR-2358). */
  contactPerson?: DayOneContactDefault;
  generatedAt?: number;
}

export interface RehydratedWizardState {
  fieldOverrides: Record<string, string>;
  /** `null` when no document recorded a choice: follow the firm default. */
  contactPerson: DayOneContactDefault | null;
}

/**
 * Rebuild the wizard's overrides and contact-person choice from the job's
 * generated documents (MOR-2358).
 *
 * Every document of one generation run carries the SAME manual-edit map, so
 * the most recently generated document that has one is authoritative for
 * the whole job: only genuine edits (blank included) come back, never a
 * resolved default such as the chosen contact person's name. Documents from
 * before the map was stored fall back to `collectSavedOverrides`, where
 * every saved value is treated as an edit.
 *
 * @example
 * rehydrateWizardState([{ fieldValues: { contact_name: "Oscar Bush" }, fieldOverrides: {}, contactPerson: { mode: "preparing_user" } }]);
 * // { fieldOverrides: {}, contactPerson: { mode: "preparing_user" } }
 */
export function rehydrateWizardState(
  precedents: ReadonlyArray<SavedWizardDocument>,
): RehydratedWizardState {
  const newestFirst = [...precedents].sort(
    (a, b) => (b.generatedAt ?? 0) - (a.generatedAt ?? 0),
  );
  const stamped = newestFirst.find((p) => p.fieldOverrides !== undefined);
  const fieldOverrides = stamped?.fieldOverrides
    ? { ...stamped.fieldOverrides }
    : collectSavedOverrides(precedents);
  const contactPerson =
    newestFirst.find((p) => p.contactPerson !== undefined)?.contactPerson ??
    null;
  return { fieldOverrides, contactPerson };
}

/** The overrides with the given keys removed (an explicit reset to defaults). */
export function withoutKeys(
  overrides: Record<string, string>,
  keys: ReadonlyArray<string>,
): Record<string, string> {
  const drop = new Set(keys);
  return Object.fromEntries(
    Object.entries(overrides).filter(([key]) => !drop.has(key)),
  );
}

/** How many of `keys` currently carry a manual override (blank included). */
export function countOverridden(
  overrides: Record<string, string>,
  keys: ReadonlyArray<string>,
): number {
  return keys.filter((key) => key in overrides).length;
}
